import React from 'react';
import { css, jsx } from '@emotion/core';
import { Controller, FieldError } from 'react-hook-form';

import t from 'react-translate';
import NvIcon from 'shared/components/nv-icon';
import { boldFontWeight, openSans } from 'styles/global_defaults/fonts';
import { gray2, primary, warning } from 'styles/global_defaults/colors';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import NvSelect, { SelectOption } from 'shared/components/inputs/nv-select';
import { doubleSpacing, halfSpacing, tripleSpacing } from 'styles/global_defaults/scaffolding';

export type Value = {
  value: CompletionCriteria,
  meta: number | string | null, // For AT_LEAST_X_COURSES value
};

type Props = {
  value: Value,
  onChange: (newValue: Value) => void,
  className?: string,
  inputError?: FieldError,
};

export enum CompletionCriteria {
  ALL_IN_ORDER = 0,
  ALL_IN_ANY_ORDER = 1,
  AT_LEAST_X_COURSES = 2,
}


const CompletionCriteriaDropdown = (props: Props) => {
  const {
    value,
    onChange,
    className,
    inputError,
  } = props;

  const options: SelectOption[] = [
    {
      label: t.LEARNING_JOURNEYS.DETAILS.COMPLETION_CRITERIA_OPTIONS.ALL_IN_ORDER(),
      value: CompletionCriteria.ALL_IN_ORDER,
    },
    {
      label: t.LEARNING_JOURNEYS.DETAILS.COMPLETION_CRITERIA_OPTIONS.ALL_IN_ANY_ORDER(),
      value: CompletionCriteria.ALL_IN_ANY_ORDER,
    },
    {
      label: t.LEARNING_JOURNEYS.DETAILS.COMPLETION_CRITERIA_OPTIONS.AT_LEAST_X_COURSES(),
      value: CompletionCriteria.AT_LEAST_X_COURSES,
    },
  ];

  const styles = css`
    display: flex;
    align-items: center;
    height: ${doubleSpacing}px;

    & > span {
      font-size: 14px;
      font-weight: 700;
      line-height: 20px;
    }

    .input {
      width: ${tripleSpacing}px;
      margin: 0 ${halfSpacing}px;

      input {
        color: ${gray2};
      }
    }

    & > .icon {
      transform: rotate(90deg);
      margin-left: ${halfSpacing}px;
    }

    & > span, input {
      font-family: ${openSans};
    }

    & > span, & > .icon {
      color: ${primary};
    }
  `;

  const handleSelectChange = (newValue) => {
    let meta = null;

    if (newValue === CompletionCriteria.AT_LEAST_X_COURSES) {
      meta = '';
    }

    onChange({
      meta,
      value: newValue,
    });
  };

  const handleInputChange = (event) => {
    const numberValue = Number(event.target.value);

    onChange({
      value: value.value,
      meta: numberValue || '',
    });
  };

  // Preventing the input to increase/decrease the number value when scrolling
  // over it
  const handleInputScroll = (event) => event.preventDefault();

  return (
    <NvSelect
      align='center'
      options={options}
      value={value.value}
      className={className}
      onChange={handleSelectChange}
      renderToggle={() => {
        const renderContent = () => {
          const handleTextInputClick = (event) => event.stopPropagation();

          switch (value.value) {
            case CompletionCriteria.ALL_IN_ORDER:
              return <span>{t.LEARNING_JOURNEYS.DETAILS.COMPLETION_CRITERIA_OPTIONS.ALL_IN_ORDER()}</span>;
            case CompletionCriteria.ALL_IN_ANY_ORDER:
              return <span>{t.LEARNING_JOURNEYS.DETAILS.COMPLETION_CRITERIA_OPTIONS.ALL_IN_ANY_ORDER()}</span>;
            case CompletionCriteria.AT_LEAST_X_COURSES:
              return (
                <React.Fragment>
                  <span>{t.LEARNING_JOURNEYS.DETAILS.CUSTOM_TOGGLE.COMPLETE_AT_LEAST()}</span>
                  <NvTextInput
                    min={1}
                    required
                    type='number'
                    className='input'
                    value={value.meta}
                    errorOnTouching={false}
                    error={inputError as any}
                    onChange={handleInputChange}
                    onScroll={handleInputScroll}
                    onClick={handleTextInputClick}
                  />
                  <span>{t.NOVOED.COURSES()}</span>
                </React.Fragment>
              );
            default: throw new Error('Unhandled value type');
          }
        };

        return (
          <div css={styles}>
            {renderContent()}
            <NvIcon
              icon='play'
              size='xss-smallest'
            />
          </div>
        );
      }}
    />
  );
};

type CompletionCriteriaLabelProps = {
  completionCriteria: Value,
  className?: string,
};

/* @ngInject */
export function CompletionCriteriaLabel({ className, completionCriteria }: CompletionCriteriaLabelProps) {
  const { value, meta } = completionCriteria;

  const styles = css`
    span {
      color: ${warning};
      font-weight: ${boldFontWeight};
    }
  `;

  const renderLabel = () => {
    switch (value) {
      case CompletionCriteria.ALL_IN_ORDER:
        return <span>{t.LEARNING_JOURNEYS.DETAILS.COMPLETION_CRITERIA_OPTIONS.ALL_IN_ORDER()}</span>;
      case CompletionCriteria.ALL_IN_ANY_ORDER:
        return <span>{t.LEARNING_JOURNEYS.DETAILS.COMPLETION_CRITERIA_OPTIONS.ALL_IN_ANY_ORDER()}</span>;
      case CompletionCriteria.AT_LEAST_X_COURSES:
        return (
          <React.Fragment>
            <span>{t.LEARNING_JOURNEYS.DETAILS.CUSTOM_TOGGLE.COMPLETE_AT_LEAST()}</span>
            <span>{` ${meta} `}</span>
          </React.Fragment>
        );
      default: throw new Error('Unhandled value type');
    }
  };

  return (
    <div className={`collection-completion-criteria${className ? ` ${className}` : ''}`} css={styles}>
      {renderLabel()}
    </div>
  );
}


type FormCompletionCriteriaDropdownProps = Omit<Props, 'value' | 'onChange'> & {
  name: string,
  defaultValue?: Value,
};

export const FormCompletionCriteriaDropdown = (props: FormCompletionCriteriaDropdownProps) => {
  const {
    name,
    defaultValue,
    ...restProps
  } = props;

  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      render={({ field: { value, onChange } }) => (
        <CompletionCriteriaDropdown
          {...restProps}
          value={value}
          onChange={onChange}
        />
      )}
    />
  );
};

export default CompletionCriteriaDropdown;
